import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { kindInfo, landingTemplates, pageKinds, slugifyPath, validatePagePath, type LandingCreate, type LandingListItem, type LocaleView, type PageKindValue, type TemplateKey } from '@agency/contracts';
import { Button, Drawer, Notice, SelectField, TextField, useToast } from '@agency/ui';
import { ApiClientError, get, post } from '../../lib/api.ts';

type Market = { code: string; name: string; enabled: boolean };
type Service = { key: string; name: string; enabled: boolean };
type Created = { variantId: string; slug: string };

const KIND_HINT: Record<PageKindValue, string> = {
  SERVICE: 'A service you sell. Indexable, lives under /services/.',
  SOLUTION: 'A packaged solution. Indexable, lives under /solutions/.',
  INDUSTRY: 'A sector page. Indexable, lives under /industries/.',
  CORE: 'Home, about, contact and other site pages at the root.',
  CAMPAIGN: 'Built for paid traffic. Hidden from search engines by default, under /lp/.',
  SEO: 'A page built to rank for one search topic, at a path you choose.',
};

function FieldErrors({ error }: { error: ApiClientError | null }) {
  if (!error) return null;
  return (
    <Notice tone="error" title="Could not save">
      {error.message}
      {error.fieldErrors.length ? <ul>{error.fieldErrors.map((f, i) => <li key={i}>{f.message}</li>)}</ul> : null}
      {error.requestId ? ` (Ref ${error.requestId})` : ''}
    </Notice>
  );
}
const fieldError = (e: ApiClientError | null, field: string) => e?.fieldErrors.find((f) => f.field === field)?.message;

/** Locales a page can be created in: enabled ones, plus any that already have pages are still selectable by an admin. */
function useLocaleOptions(enabled: boolean) {
  return useQuery<LocaleView[], ApiClientError>({ queryKey: ['locale-options'], queryFn: () => get('/admin/site/locale-options'), enabled, staleTime: 30_000 });
}

const STEPS = ['Purpose', 'Focus', 'Template', 'Name and URL', 'Review'] as const;

/** Guided creation (step 1 §15): choose what the page is for, then it starts from a structured template with clearly marked placeholder copy. */
export function NewLandingDrawer({ open, onClose }: { open: boolean; onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const locales = useLocaleOptions(open);
  const markets = useQuery<Market[]>({ queryKey: ['markets'], queryFn: () => get('/admin/site/markets'), enabled: open, staleTime: 30_000 });
  const services = useQuery<Service[]>({ queryKey: ['services'], queryFn: () => get('/admin/site/services'), enabled: open, staleTime: 30_000 });
  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<PageKindValue>('SERVICE');
  const [serviceKey, setServiceKey] = useState('');
  const [marketCode, setMarketCode] = useState('');
  const [locale, setLocale] = useState('');
  const [restricted, setRestricted] = useState(false);
  const [template, setTemplate] = useState<TemplateKey>('lead-gen-minimal');
  const [name, setName] = useState('');
  const [slugEdit, setSlugEdit] = useState<string | null>(null);

  const defaultLocale = locales.data?.find((l) => l.isDefault)?.code ?? 'en';
  const loc = locale || defaultLocale;
  const info = kindInfo[kind];
  const derived = useMemo(() => (name.trim() ? `${info.prefix}${slugifyPath(name, loc)}` : ''), [name, loc, info.prefix]);
  const slug = slugEdit ?? derived;
  const slugProblem = slug ? validatePagePath(slug) : null;

  const create = useMutation<Created, ApiClientError, LandingCreate>({
    mutationFn: (body) => post('/admin/landings', body),
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['landings'] });
      toast({ tone: 'success', message: `Draft created at /${r.slug}. Replace the placeholder copy before publishing.` });
      reset();
      onClose();
      nav(`/landings/${r.variantId}`);
    },
  });
  const reset = () => { setStep(0); setKind('SERVICE'); setServiceKey(''); setMarketCode(''); setLocale(''); setRestricted(false); setTemplate('lead-gen-minimal'); setName(''); setSlugEdit(null); create.reset(); };
  const close = () => { reset(); onClose(); };

  const canNext = step === 3 ? name.trim().length >= 2 && !slugProblem && Boolean(slug) : true;
  const submit = () => create.mutate({ name: name.trim(), kind, locale: loc, slug, serviceKey: serviceKey || undefined, marketCode: marketCode || undefined, template, restrictedCategory: restricted });

  return (
    <Drawer open={open} onClose={close} title="New landing page" width={560}
      footer={<>
        <Button onClick={step === 0 ? close : () => setStep(step - 1)}>{step === 0 ? 'Cancel' : 'Back'}</Button>
        {step < STEPS.length - 1
          ? <Button variant="primary" disabled={!canNext} onClick={() => setStep(step + 1)}>Next: {STEPS[step + 1]}</Button>
          : <Button variant="primary" loading={create.isPending} onClick={submit}>Create draft</Button>}
      </>}>
      <ol className="wizard-steps" aria-label="Steps">
        {STEPS.map((s, i) => <li key={s} aria-current={i === step ? 'step' : undefined} data-done={i < step || undefined}>{s}</li>)}
      </ol>
      <div className="wizard-body">
        {step === 0 ? (
          <fieldset className="rep-set">
            <legend>What is this page for?</legend>
            {pageKinds.map((k) => (
              <label key={k} className="choice">
                <input type="radio" name="kind" value={k} checked={kind === k} onChange={() => { setKind(k); setSlugEdit(null); }} data-autofocus={k === kind ? true : undefined} />
                <span><strong>{kindInfo[k].label}</strong><br /><span className="ag-hint">{KIND_HINT[k]}</span></span>
              </label>
            ))}
          </fieldset>
        ) : null}
        {step === 1 ? (
          <>
            <SelectField label="Language" value={loc} onChange={(e) => { setLocale(e.target.value); setSlugEdit(null); }} hint="The language this version is written in. Translations are added later from the page.">
              {(locales.data ?? []).map((l) => <option key={l.code} value={l.code}>{l.name}{l.enabled ? '' : ' (not enabled yet)'}</option>)}
            </SelectField>
            <SelectField label="Service (optional)" value={serviceKey} onChange={(e) => setServiceKey(e.target.value)} hint="Links the page to a service for reporting and related-service blocks.">
              <option value="">None</option>
              {(services.data ?? []).filter((s) => s.enabled).map((s) => <option key={s.key} value={s.key}>{s.name}</option>)}
            </SelectField>
            <SelectField label="Target market (optional)" value={marketCode} onChange={(e) => setMarketCode(e.target.value)}>
              <option value="">No specific market</option>
              {(markets.data ?? []).filter((m) => m.enabled).map((m) => <option key={m.code} value={m.code}>{m.name}</option>)}
            </SelectField>
            <label className="choice">
              <input type="checkbox" checked={restricted} onChange={(e) => setRestricted(e.target.checked)} />
              <span><strong>Restricted category</strong><br /><span className="ag-hint">Health, finance, legal or similar topics where ad platforms apply extra policies. Flags the page for review before any advertising.</span></span>
            </label>
          </>
        ) : null}
        {step === 2 ? (
          <fieldset className="rep-set">
            <legend>Starting structure</legend>
            {landingTemplates.map((t) => (
              <label key={t.key} className="choice">
                <input type="radio" name="template" value={t.key} checked={template === t.key} onChange={() => setTemplate(t.key)} />
                <span><strong>{t.label}</strong><br /><span className="ag-hint">{t.description} ({t.sections.length} sections)</span></span>
              </label>
            ))}
          </fieldset>
        ) : null}
        {step === 3 ? (
          <>
            <TextField label="Page name" required value={name} maxLength={120} onChange={(e) => { setName(e.target.value); }} hint="Internal name. The search title is set in the editor." error={fieldError(create.error, 'name')} data-autofocus />
            <TextField label="URL path" required value={slug} onChange={(e) => setSlugEdit(e.target.value)} className="ag-mono"
              hint={`Derived from the name${info.prefix ? ` under ${info.prefix}` : ''}. Lowercase letters, numbers and hyphens. The language prefix is added automatically.`}
              error={slugProblem ?? fieldError(create.error, 'slug')} />
          </>
        ) : null}
        {step === 4 ? (
          <dl className="summary">
            <dt>Type</dt><dd>{info.label}</dd>
            <dt>Name</dt><dd>{name}</dd>
            <dt>Language</dt><dd>{locales.data?.find((l) => l.code === loc)?.name ?? loc}</dd>
            <dt>Path</dt><dd className="ag-mono">/{slug}</dd>
            <dt>Template</dt><dd>{landingTemplates.find((t) => t.key === template)?.label}</dd>
            <dt>Indexing</dt><dd>{info.indexDefault ? 'Allowed once published' : 'Hidden from search engines (campaign page)'}</dd>
            {restricted ? <><dt>Restricted</dt><dd>Yes</dd></> : null}
          </dl>
        ) : null}
        {step === 4 ? <p className="ag-hint">The draft starts with placeholder copy marked “[Edit]”. Nothing is invented for you, and the page cannot be published until those are replaced.</p> : null}
        <FieldErrors error={create.error} />
      </div>
    </Drawer>
  );
}

export function DuplicateDrawer({ item, onClose }: { item: LandingListItem | null; onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState('');
  const m = useMutation<Created, ApiClientError, void>({
    mutationFn: () => post(`/admin/landings/${item!.id}/duplicate`, { name: name.trim() }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['landings'] }); toast({ tone: 'success', message: `Duplicated to /${r.slug} as a new draft.` }); setName(''); onClose(); nav(`/landings/${r.variantId}`); },
  });
  return (
    <Drawer open={item !== null} onClose={() => { m.reset(); onClose(); }} title={`Duplicate “${item?.name ?? ''}”`}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={name.trim().length < 2} onClick={() => m.mutate()}>Create copy</Button></>}>
      <p className="ag-hint">The copy is a separate page with its own URL and history. It starts as an unpublished draft of the current draft content.</p>
      <TextField label="Name of the copy" required value={name} onChange={(e) => setName(e.target.value)} data-autofocus error={fieldError(m.error, 'name')} />
      <FieldErrors error={m.error} />
    </Drawer>
  );
}

export function TranslateDrawer({ item, existing, onClose }: { item: LandingListItem | null; existing: string[]; onClose: () => void }) {
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const locales = useLocaleOptions(item !== null);
  const [locale, setLocale] = useState('');
  const [slug, setSlug] = useState('');
  const options = (locales.data ?? []).filter((l) => !existing.includes(l.code));
  const chosen = locale || options[0]?.code || '';
  const m = useMutation<Created, ApiClientError, void>({
    mutationFn: () => post(`/admin/landings/${item!.id}/translate`, { locale: chosen, ...(slug.trim() ? { slug: slug.trim() } : {}) }),
    onSuccess: (r) => { void qc.invalidateQueries({ queryKey: ['landings'] }); toast({ tone: 'success', message: `Draft created in the new language at /${r.slug}.` }); setSlug(''); onClose(); nav(`/landings/${r.variantId}`); },
  });
  return (
    <Drawer open={item !== null} onClose={() => { m.reset(); onClose(); }} title={`Add a language to “${item?.name ?? ''}”`}
      footer={<><Button onClick={onClose}>Cancel</Button><Button variant="primary" loading={m.isPending} disabled={!chosen} onClick={() => m.mutate()}>Create translation draft</Button></>}>
      <p className="ag-hint">This copies the source content into a new draft for a person to translate. Nothing is machine-translated or published automatically, and the glossary rules are shown in the editor.</p>
      {options.length === 0 && !locales.isLoading ? <Notice tone="info" title="All languages are covered">Every configured language already has a version of this page. Add a language in Settings first.</Notice> : (
        <>
          <SelectField label="Language" value={chosen} onChange={(e) => setLocale(e.target.value)} data-autofocus>
            {options.map((l) => <option key={l.code} value={l.code}>{l.name}{l.enabled ? '' : ' (not enabled yet)'}</option>)}
          </SelectField>
          <TextField label="URL path in that language (optional)" value={slug} onChange={(e) => setSlug(e.target.value)} className="ag-mono" hint="Leave empty to reuse the source path. Translated paths are better for search, for example dienstleistungen/crm-entwicklung." error={fieldError(m.error, 'slug')} />
        </>
      )}
      <FieldErrors error={m.error} />
    </Drawer>
  );
}
